/**
 * platform-guide.ts — the in-app support AI's knowledge of HOW Breakin' Bread
 * works and WHERE things live. Feeds the support chat so it can teach users to
 * do and find things ("how do I publish?", "where's my wallet?").
 *
 * This is a distilled, task-focused mirror of the human Help page
 * (src/app/help/page.tsx). Keep the two roughly in sync when features change.
 * Text-only on purpose (no icons/components) so it's cheap to ship in the chat
 * bundle and cheap to put in an LLM prompt.
 */

export interface GuideEntry {
  /** Short task/area name. */
  area: string;
  /** Where it lives in the app (sidebar item / route / button). */
  where: string;
  /** Lowercase keywords used to match a user's question. */
  keywords: string[];
  /** Ordered how-to steps. */
  steps: string[];
}

/** Quick "where things live" map — always given to the AI. */
export const NAV_MAP = `WHERE THINGS LIVE (left sidebar → route):
- Home /home · Music /music · Artists /artists · Charts /charts · Events /events
- News /news · Radio /radio · Marketplace /marketplace · Playlists /playlists
- Videos /videos · Community (Wall + Jam Session + Listening Party) /community
- Studio — one "make" roof: Artwork /artwork-studio · Film /film-maker · DJ /dj ·
  Recorder /studio/recorder · Songwriter /studio/songwriter · Live /studio/live ·
  Jam Session /studio/jam-session · Podcast /studio/podcast
- Money hub: Wallet /wallet · Earnings /earnings · Bookkeeping /bookkeeping
- Media Vault (upload/manage your works) /media · Publishing /publishing
- Sync /synchronization · Legal AI /legal-ai · Creator AI (Advisor) /creator-ai
- Profile /profile · Settings /settings · Help /help · Why BB /why-bb
The chat button (bubble, bottom-right) is always visible; it has Chat, Support
(this guide) and Advisor tabs.`;

export const PLATFORM_GUIDE: GuideEntry[] = [
  {
    area: "Create an account / onboarding",
    where: "Sign up, then the /onboarding screen",
    keywords: ["sign up", "signup", "register", "account", "onboarding", "join", "log in", "login", "get started", "create account"],
    steps: [
      "Tap Sign Up (or the chat button as a visitor — it routes you to sign-in).",
      "Sign in with Google or email.",
      "On the onboarding screen pick your account type: consumer (fan), business, creator, or venue.",
      "Creators pick their specific roles (artist, producer, etc.).",
      "Finish — you land on Home (fans) or your Profile (creators/venues).",
    ],
  },
  {
    area: "Find and play music",
    where: "Music /music, Artists /artists, Search (top bar)",
    keywords: ["find", "play", "listen", "music", "song", "track", "search", "discover", "stream"],
    steps: [
      "Open Music in the sidebar, or use the Search box in the header to search by title or artist.",
      "Tap any track to play it in the media player at the bottom.",
      "Open an artist from Artists to see their releases, tracks and videos.",
      "Heart a track to save it to your favorites bar.",
    ],
  },
  {
    area: "Use the media player",
    where: "Player bar at the bottom of every page",
    keywords: ["player", "play", "pause", "queue", "volume", "repeat", "shuffle", "download button", "next", "previous"],
    steps: [
      "Play/pause, skip, shuffle and repeat are in the bottom player bar.",
      "Tap the player (or the up-arrow on mobile) to open the full player sheet with the queue and artwork.",
      "Long titles scroll automatically so you can read them.",
      "Tap the release/album title in the player to open that release page.",
      "Use the download button in the player to buy/download the track (if the maker allows it).",
    ],
  },
  {
    area: "Upload a single track",
    where: "Media Vault /media → Single Upload",
    keywords: ["upload", "single", "add track", "publish", "release music", "put my music", "distribute"],
    steps: [
      "Open Media Vault (/media) in the sidebar.",
      "Choose Single Upload.",
      "Add the audio file, cover art, title, artist, genre(s), ISRC and credits.",
      "Set your price option (free, pay-what-you-feel, or fixed) and whether downloads are allowed.",
      "Publish — the track appears in your Media Vault and on your artist/profile pages.",
      "Then register the work with your copyright societies (see 'Register with a copyright society').",
    ],
  },
  {
    area: "Upload an album or EP (multiple tracks)",
    where: "Media Vault /media → Album/EP Upload",
    keywords: ["album", "ep", "mixtape", "bulk upload", "multiple tracks", "release", "project", "compilation"],
    steps: [
      "Open Media Vault (/media) → Album/EP Upload.",
      "Pick a Release Type (Album, EP, Single, Mixtape, Compilation, Live Album).",
      "Drop in all the audio files; metadata (title/artist/ISRC) is read from the files where possible.",
      "Add shared artwork (up to 8 images; the first is the cover), year, label and credits.",
      "Optionally use 'Extract Credits with AI' on a liner-notes photo, or 'Parse Credits from Text'.",
      "Publish — BB auto-creates a Release that links all the tracks, so it shows on Home's new releases and your artist page.",
    ],
  },
  {
    area: "Set a price — free, pay-what-you-feel, or fixed",
    where: "In the upload/edit form, per release",
    keywords: ["price", "pricing", "pay what you feel", "pwyw", "name your price", "free", "paid", "sell", "how much", "charge"],
    steps: [
      "In the track/release form choose a download type: Free, Pay-what-you-feel (PWYW), or Fixed price.",
      "For PWYW set a minimum (floor) price; for a whole release the minimum is €1 per track.",
      "Fans can always pay above the floor — anything extra is kept 100% by you.",
      "BB's cut is π% (~3.14%) of the floor/sale only; the rest goes to you after the card fee.",
    ],
  },
  {
    area: "Download a track or release",
    where: "Download button on a track/release page or in the player",
    keywords: ["download", "buy", "purchase", "get the file", "zip", "download all"],
    steps: [
      "Open the track or release and tap Download (or use the player's download button).",
      "Free items download immediately; PWYW/paid items open a checkout — enter your amount and pay.",
      "'Download All' on a release bundles every track into a ZIP (also passes through the price/checkout).",
      "Each download is a numbered edition stamped with your name — a real collector's copy.",
    ],
  },
  {
    area: "Register with a copyright society (STIM + SAMI)",
    where: "External societies; guidance in Help → Publishing",
    keywords: ["stim", "sami", "royalties", "copyright", "pro", "publishing rights", "neighbouring rights", "collecting society", "register song", "ascap", "bmi", "prs", "gema"],
    steps: [
      "BB is not a collecting society — there are TWO royalties to claim, from TWO societies.",
      "① The SONG (composition you wrote) → a PRO: STIM (Sweden), or ASCAP/BMI (US), PRS (UK), GEMA (DE), etc. Register the work with matching title, splits and ISWC.",
      "② The RECORDING (your performance) → a performer/neighbouring-rights society: SAMI (Sweden), PPL (UK), SoundExchange (US), GVL (DE), etc. Register with the ISRC.",
      "Use the same credits and codes as on BB so royalties can find you.",
      "Do this every time you publish. Full country list: Help → Publishing.",
    ],
  },
  {
    area: "Sell in the Marketplace / Name Your Price",
    where: "Marketplace /marketplace; manage from Profile → Marketplace",
    keywords: ["marketplace", "sell", "merch", "service", "buy", "make an offer", "name your price", "report", "listing", "shop"],
    steps: [
      "Browse Marketplace (/marketplace); toggle grid/list/compact and sort by price or name.",
      "To list an item: Profile → Marketplace → add item (title, price, images, digital or physical).",
      "Buyers tap an item then Buy, or 'Name Your Price' on PWYW/digital items.",
      "Report a listing with the Report button (goes to admins).",
    ],
  },
  {
    area: "Chat, Support and the Creator Advisor",
    where: "Chat button (bubble), bottom-right, always visible",
    keywords: ["chat", "message", "support", "help", "advisor", "creator ai", "contact", "dm", "talk"],
    steps: [
      "Tap the chat bubble (bottom-right) — it's always there, even logged out.",
      "Chat tab: message your contacts (people you follow / who follow you).",
      "Support tab: ask me how to do or find anything on BB (this guide).",
      "Advisor tab: the Creator Advisor AI for legal, finance, marketing, publishing and sync advice.",
    ],
  },
  {
    area: "Turn on push notifications",
    where: "Profile → Notifications → Push (this device)",
    keywords: ["notification", "notifications", "push", "alerts", "pwa", "notify"],
    steps: [
      "Open Profile → Notifications and find 'Push (this device)'.",
      "Toggle it on and allow notifications when your browser/phone asks.",
      "On iPhone/iPad you must first Add BB to your Home Screen (Share → Add to Home Screen), then open it from that icon.",
      "Use 'Send test' to confirm it works.",
    ],
  },
  {
    area: "Your Profile and Wall Studio",
    where: "Profile /profile",
    keywords: ["profile", "wall", "customize", "view as visitor", "bio", "posts", "pin", "wall studio", "playlists tab"],
    steps: [
      "Open Profile (/profile). Tabs: Wall, Music, Videos, Marketplace, Playlists.",
      "Edit your wall look in Wall Studio (themes, background, colors, fonts) and pin content blocks.",
      "Use 'View as Visitor' (eye button) to preview what the public sees.",
      "The Rights & Fees panel explains what you keep and BB's π% cut.",
    ],
  },
  {
    area: "The public Wall (add blocks, full-screen, exit)",
    where: "Community → Wall, and your /wall/[you]",
    keywords: ["wall", "add track", "sticky note", "add block", "drawing", "full screen", "immersion", "escape", "exit wall", "pin"],
    steps: [
      "Open your wall; owners get an add menu to drop blocks: track, release, artist, note, image, link, video.",
      "Search and pick an item for a track/release block, or paste a URL to auto-embed (YouTube, Spotify, SoundCloud, image, PDF).",
      "Tap the maximize button for full-immersion mode.",
      "To exit full immersion press Esc (or use 'Show controls' / 'Exit' at the top-center).",
    ],
  },
  {
    area: "Radio and AI DJs",
    where: "Radio /radio",
    keywords: ["radio", "dj", "ai dj", "station", "stream radio", "lingo", "persona"],
    steps: [
      "Open Radio (/radio) and pick a station to start listening.",
      "AI DJs introduce tracks; you can train a DJ's persona/lingo and how often it talks (every track, every other, every third).",
      "The now-playing artwork shows on the station and fades to the next song's art.",
    ],
  },
  {
    area: "The Studio hub (make tools)",
    where: "Studio — /artwork-studio and the /studio/* tabs",
    keywords: ["studio", "artwork", "film", "video maker", "dj studio", "recorder", "songwriter", "live stream", "jam session", "podcast", "make", "create"],
    steps: [
      "Studio is one roof over the make tools; use the tab strip at the top.",
      "Artwork Studio (cover art), Film Maker (lyric/music videos), DJ, Recorder, Songwriter.",
      "Live = live-streaming studio; Jam Session = real-time multi-device audio collaboration; Podcast = record/upload podcasts.",
    ],
  },
  {
    area: "Community (Wall topics, Jam Session, Listening Party)",
    where: "Community /community",
    keywords: ["community", "forum", "topics", "reddit", "jam session", "listening party", "party", "discussion"],
    steps: [
      "Community has three tabs: Wall (Reddit-style topics), Jam Session, and Listening Party.",
      "Post in a topic (BB platform, music, music tech, music business) or start your own.",
      "Jam Session: create or join by code for a live audio jam.",
      "Listening Party: sync playback across devices; share a code so friends listen in time.",
    ],
  },
  {
    area: "Wallet, Earnings and getting paid (payouts)",
    where: "Money hub: Wallet /wallet, Earnings /earnings",
    keywords: ["wallet", "earnings", "payout", "get paid", "stripe", "connect", "set up payouts", "balance", "withdraw", "money"],
    steps: [
      "Open Wallet (/wallet) to see your balance and Earnings (/earnings) for a breakdown.",
      "Tap 'Set Up Payouts' to connect Stripe (you'll be redirected to Stripe onboarding and back).",
      "Once connected, sales route to your account minus BB's π% fee; request a payout from the wallet.",
      "Your account needs an email on file (Settings) for Stripe.",
    ],
  },
  {
    area: "Bookkeeping (scan receipts, categories)",
    where: "Money hub → Bookkeeping /bookkeeping",
    keywords: ["bookkeeping", "receipt", "scan", "expenses", "accounting", "categories", "bas", "entries", "invoice", "tax", "ocr"],
    steps: [
      "Open Bookkeeping (/bookkeeping).",
      "Add an entry, or scan a receipt: use 'Take photo' (camera) or upload a photo/PDF — it's OCR'd into the books.",
      "Assign a category; enable the standard Swedish BAS accounts or add your own custom categories in Settings.",
      "Review and edit any entry after scanning.",
    ],
  },
  {
    area: "Publishing & Sync licensing",
    where: "Publishing /publishing, Sync /synchronization",
    keywords: ["publishing", "sync", "license", "briefs", "royalty dashboard", "placement", "unclaimed", "tv film ad"],
    steps: [
      "Publishing (/publishing): Royalties tab (earnings by track), Unclaimed tab (register works with a PRO), Verification tab.",
      "Sync (/synchronization): browse briefs from TV/film/ad/game clients.",
      "Open a brief and Submit a fitting track; track its status in the Placements tab.",
    ],
  },
  {
    area: "Legal AI (rights + contracts)",
    where: "Legal AI /legal-ai",
    keywords: ["legal", "contract", "rights checker", "analyze contract", "scan contract", "ocr", "counter proposal", "deadline", "reminder", "clause", "isrc check", "law"],
    steps: [
      "Open Legal AI (/legal-ai).",
      "Active Issues flags rights conflicts, unregistered works and missing splits.",
      "Rights Checker: enter a track or ISRC to check ownership/registration.",
      "Contract Scanner: take photos of each page or upload a PDF (or paste the text) → Scan contract (10 tokens up to 20 pages). You get a plain-language summary, key terms, red flags and every date in the contract.",
      "Dates & reminders: fix any date, tap Email me (60/30/14/7/1 days before or on the day) and Add to calendar for phone alarms.",
      "Counter-proposal: tick the red flags to change, add notes, pick tone + language → Write email (2 tokens) → Open in email app. You send it yourself.",
      "Ask: questions about that contract (2 tokens each). Contracts are private — only you can see them.",
    ],
  },
  {
    area: "Share anything with a QR code",
    where: "QR button on artists, music, marketplace, events, etc.",
    keywords: ["qr", "share", "link", "code", "poster"],
    steps: [
      "Tap the QR button on almost any page (artist, release, item, event, profile...).",
      "A full-screen QR + link opens; use Copy Link or Save QR to share it anywhere.",
    ],
  },
  {
    area: "Settings & language",
    where: "Settings /settings",
    keywords: ["settings", "language", "email", "password", "account settings", "layout", "sidebar order", "preferences", "delete account"],
    steps: [
      "Open Settings (/settings) for account, email/password, language and notification preferences.",
      "BB auto-detects your language; change it anytime.",
      "Reorder your sidebar by dragging items (the order is saved to your account).",
    ],
  },
  {
    area: "Fees — what BB takes (π%)",
    where: "Rights & Fees (Profile, Help)",
    keywords: ["fee", "fees", "cut", "commission", "percentage", "pi", "π", "take", "bb take", "you take", "what do you take", "how much do you take", "your cut", "3.14"],
    steps: [
      "BB takes π% (about 3.14%) of each sale and PWYW download — the smallest, strangest cut in music.",
      "The rest (~96.9%) goes to the maker after the card processor's fee; gifts above your floor are 100% yours.",
      "Merch (print-on-demand) is priced separately (its margin mostly covers the print partner's cost).",
      "The goal is to take less over time as the community grows.",
    ],
  },
];

const STOPWORDS = new Set([
  "the", "and", "for", "you", "your", "this", "that", "with", "from", "are",
  "can", "does", "did", "get", "got", "use", "using", "into", "out", "off",
  "how", "what", "where", "when", "why", "who", "will", "would", "should",
  "have", "has", "was", "were", "but", "not", "any", "all", "one", "our",
  "app", "bread", "breakin", "want", "need", "find", "them", "then", "here",
]);

const words = (s: string) =>
  s.toLowerCase().split(/[^a-z0-9π]+/).filter((w) => w.length >= 2 && !STOPWORDS.has(w));

/** Score an entry against a question (keyword + phrase overlap). */
function scoreEntry(entry: GuideEntry, q: string): number {
  const ql = q.toLowerCase();
  let score = 0;
  for (const kw of entry.keywords) {
    if (ql.includes(kw)) score += kw.includes(" ") ? 3 : 2;
  }
  const qWords = new Set(words(q));
  const hay = words(entry.area + " " + entry.where + " " + entry.steps.join(" "));
  for (const w of hay) if (qWords.has(w)) score += 1;
  return score;
}

/**
 * Build the guide context for the LLM: the nav map + a full catalog of areas
 * (always), plus detailed steps for the handful of areas most relevant to the
 * question. Keeps prompts affordable while covering the whole app.
 */
export function buildPlatformGuideForAI(question = ""): string {
  const catalog = PLATFORM_GUIDE.map((e) => `- ${e.area} → ${e.where}`).join("\n");

  const ranked = PLATFORM_GUIDE
    .map((e) => ({ e, s: scoreEntry(e, question) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, 4);

  const deep = ranked
    .map(({ e }) =>
      `### ${e.area}\nWhere: ${e.where}\n` +
      e.steps.map((st, i) => `${i + 1}. ${st}`).join("\n")
    )
    .join("\n\n");

  return [
    NAV_MAP,
    `\nEVERYTHING BB CAN DO (area → where it lives):\n${catalog}`,
    deep ? `\nSTEP-BY-STEP for what the user is asking about:\n${deep}` : "",
  ].filter(Boolean).join("\n");
}

/**
 * Offline how-to answer built directly from the guide — used as a fallback when
 * the AI API key is missing or the AI call fails, so "how do I…" still works.
 * Returns null when nothing matches confidently.
 */
export function answerHowToLocally(question: string): string | null {
  const ranked = PLATFORM_GUIDE
    .map((e) => ({ e, s: scoreEntry(e, question) }))
    .sort((a, b) => b.s - a.s);
  const top = ranked[0];
  // Require a real signal (a keyword hit is worth ≥2) so generic/off-topic
  // questions fall through to the AI/search instead of a forced match.
  if (!top || top.s < 3) return null;
  const e = top.e;
  return (
    `Here's how: **${e.area}**\nWhere: ${e.where}\n\n` +
    e.steps.map((st, i) => `${i + 1}. ${st}`).join("\n") +
    `\n\nWant more detail on any step? Just ask.`
  );
}
